import { useState, useEffect, Children, cloneElement } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';

import styles from './Dropdown.module.scss';

const cx = classNames.bind(styles);

const Dropdown = ({
  className = '',
  title,
  arrowIcon = false,
  children,
  position = 'left',
  color = 'dark',
  textTransform = 'uppercase',
  dataTest = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const handleFocus = () => {
    setIsActive(true);
  };

  const handleBlur = () => {
    setIsActive(false);
  };

  const listenOutsideClick = () => {
    // eslint-disable-next-line no-use-before-define
    window.addEventListener('click', collapse, false);
  };

  const unlistenOutsideClick = () => {
    // eslint-disable-next-line no-use-before-define
    window.removeEventListener('click', collapse, false);
  };

  const handleToggleDropdown = () => {
    if (!isOpen) {
      listenOutsideClick();
    } else {
      unlistenOutsideClick();
    }
    setIsOpen(!isOpen);
  };

  const handleValidClick = e => {
    if (e.target === e.currentTarget) {
      e.stopPropagation();
    }
  };

  const collapse = () => {
    if (!isActive) {
      setIsOpen(false);
      unlistenOutsideClick();
    }
  };

  useEffect(() => {
    return () => {
      unlistenOutsideClick();
    };
  }, []);

  const btnClassName = cx(
    'dropdown',
    {
      active: isOpen,
      left: position === 'left'
    },
    className,
    styles[color]
  );

  const btnActionClassName = cx(
    'action',
    styles[textTransform],
    styles[className]
  );

  return (
    <div
      className={btnClassName}
      onMouseEnter={handleToggleDropdown}
      onMouseLeave={handleToggleDropdown}
      onClick={handleToggleDropdown}
      onBlur={handleBlur}
      onFocus={handleFocus}
      role="button"
      tabIndex="-1"
      data-test={dataTest}
    >
      <div className={btnActionClassName}>
        <span>{title}</span>
        {arrowIcon && (
          <div className={styles['icon-container']}>
            <i className="icon icon--angle-down" />
          </div>
        )}
      </div>
      {isOpen && (
        <div
          role="button"
          tabIndex="-1"
          className={styles.content}
          onClick={handleValidClick}
        >
          {Children.map(children, child => cloneElement(child, { color }))}
        </div>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  arrowIcon: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  position: PropTypes.oneOf(['left', 'right']),
  color: PropTypes.oneOf(['dark', 'light']),
  textTransform: PropTypes.oneOf(['uppercase', 'capitalize']),
  dataTest: PropTypes.string
};

export default Dropdown;
